import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { memo, useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import { compose, prop, keys, groupBy, equals, propEq, pick } from "ramda";
import { initGraph, updateAxes, updateGridLines } from "@dataVisualizations/common/common";
import { initBarchart, updateCi, updatePreviousMeasurements, updateLatestMeasurements, updateSoughtValue, updateReichAverage } from "./renderFunctions";
import { getColorUsingOption, targetColor } from "utils/colors";
import { sort, sortOrganizationIdsByPeriodValue } from "utils/sort";
import { allGenderTypes } from "utils/genders";
import { getPeriod, previousPeriod, samePeriod } from "utils/periods";
import { ellipsis } from "utils/format";
import { extractValue } from "utils/measurement";
import { useUnitListsContext } from "@unitLists";
import { getPaddedMax, getPaddedMin } from "@dataVisualizations/dataVizualizations.utils";
import useGraphRender from "@dataVisualizations/hooks/useGraphRender";
import { measurementsParams } from "utils/measurementsWithMetadata";
import { jsx as _jsx } from "react/jsx-runtime";
var margin = {
  left: 15,
  right: 25,
  bottom: 50,
  top: 20
};
export default /*#__PURE__*/memo(function BarchartGraph(props) {
  var query = props.query,
    measure = props.measure,
    measurementsWithMetadata = props.measurementsWithMetadata,
    focusId = props.focusId,
    prevFocusId = props.prevFocusId,
    loading = props.loading,
    containerRef = props.containerRef,
    lookupOrganization = props.lookupOrganization,
    interactivity = props.interactivity;
  var genderKeys = Object.keys(allGenderTypes);
  var graphRef = useRef();
  var description = useRef();
  var hydratedTargets = useRef((measure === null || measure === void 0 ? void 0 : measure.targetMeasurements) || []);
  var measurementsWithMetadataRef = useRef(measurementsWithMetadata);
  var _useUnitListsContext = useUnitListsContext(),
    activeUnitList = _useUnitListsContext.activeUnitList;
  var multipleGenders = measurementsWithMetadata && compose(function (x) {
    return x.length === 2;
  }, keys, groupBy(prop("measureId")))(measurementsWithMetadata);
  useEffect(function () {
    var _initGraph = initGraph(graphRef.current, margin),
      chart = _initGraph.chart,
      svg = _initGraph.svg;
    description.current = initBarchart(chart, svg);
  }, []);
  useEffect(function () {
    measurementsWithMetadataRef.current = measurementsWithMetadata;
  }, [measurementsWithMetadata]);
  var setDescription = useCallback(function (value, color, org, x, y) {
    description.current.selectAll("rect").attr("stroke", color);
    description.current.select(".value").text(ellipsis(21, value)).style("font-size", "18px").style("font-family", "Roboto-bold").style("font-weight", "normal").attr("transform", "translate(12, 30)");
    description.current.select(".organization").text(ellipsis(24, org)).style("font-family", "Roboto-regular");
    var top = y <= 74 ? y + 20 + margin.top : +y - 90 + margin.top;
    var edgeLeft = 10;
    var edgeRight = containerRef.current.offsetWidth - margin.right - 220;
    var max = Math.max(edgeLeft, x - 65 + margin.left);
    var left = Math.min(max, edgeRight);
    var translation = "translate(".concat(left, ",").concat(top, ")");
    if (description.current.attr("transform") === "translate(0,0)") description.current.attr("transform", translation);
    description.current.transition().attr("transform", translation).attr("opacity", 1);
  }, [containerRef]);
  var addTargetHover = useCallback(function () {
    var _hydratedTargets$curr;
    var targetIds = ((_hydratedTargets$curr = hydratedTargets.current) === null || _hydratedTargets$curr === void 0 ? void 0 : _hydratedTargets$curr.map(function (target) {
      return target.Id;
    })) || [];
    var idx = targetIds.indexOf(focusId);
    var target = hydratedTargets.current[idx];
    d3.selectAll(".hover-description-group").selectAll(".manymeasure-target-value").transition().attr("opacity", 0.1);
    d3.selectAll(".hover-description-group").selectAll(".target".concat(idx)).attr("stroke-width", 5).attr("stroke-dasharray", "6,6").transition().attr("opacity", 1);
    setDescription(target.Value, targetColor(idx), target.AuthoringOrganization, d3.selectAll(".manymeasure-target-value").attr("x1"), -35);
  }, [focusId, setDescription]);
  var removeTargetHover = useCallback(function () {
    d3.selectAll(".hover-description-group").selectAll(".manymeasure-target-value").attr("stroke-width", 3).attr("stroke-dasharray", "3,3").transition().attr("opacity", 1);
    description.current.transition().attr("opacity", 0);
  }, []);
  useEffect(function () {
    if (!equals(prevFocusId, focusId)) {
      var previousWasTarget = hydratedTargets === null || hydratedTargets === void 0 ? void 0 : hydratedTargets.current.find(function (x) {
        return x.Id === prevFocusId;
      });
      var currentIsTarget = hydratedTargets === null || hydratedTargets === void 0 ? void 0 : hydratedTargets.current.find(function (x) {
        return x.Id === focusId;
      });
      if (previousWasTarget && !currentIsTarget) removeTargetHover();
      if (currentIsTarget) addTargetHover();
    }
  }, [focusId, removeTargetHover, addTargetHover, prevFocusId]);
  var renderGraph = function renderGraph() {
    var _query$sort;
    var _measure$measureUnit = measure.measureUnit,
      measureUnit = _measure$measureUnit === void 0 ? "" : _measure$measureUnit,
      targetMeasurements = measure.targetMeasurements;
    var targetValues = (targetMeasurements === null || targetMeasurements === void 0 ? void 0 : targetMeasurements.map(prop("Value"))) || [];
    var svg = d3.select(graphRef.current);
    var rowHeight = multipleGenders ? 19 : 34;
    var amountOfRows = query.units.length * (multipleGenders ? 2 : 1);
    var idxMultiplier = multipleGenders ? function (idx) {
      return Math.floor(idx / 2);
    } : function (idx) {
      return idx;
    };
    var genderOpacity = multipleGenders ? function (d, idx) {
      idx % 2 === 0 ? 1 : 0.7;
    } : 1;
    var barPadding = multipleGenders ? 2 : 4;
    var barHeight = rowHeight;
    var paddedBarHeight = Math.max(barHeight - barPadding * 2, 5);
    var previousBarHeight = Math.max(barHeight / 2 - barPadding * 2, 5);
    var calcYPos = function calcYPos(i) {
      return multipleGenders && i % 2 === 0 ? i * Math.max(barHeight, 5) + (barPadding + 2) + margin.top : i * Math.max(barHeight, 5) + barPadding + margin.top;
    };
    var calcHeight = function calcHeight() {
      return amountOfRows * rowHeight + margin.top + margin.bottom;
    };
    var svgHeight = calcHeight();
    var genderSortedMeasurementsWithMetadata = sort(function (x) {
      var _measure$submeasures, _measure$submeasures$;
      return genderKeys.indexOf((_measure$submeasures = measure.submeasures) === null || _measure$submeasures === void 0 ? void 0 : (_measure$submeasures$ = _measure$submeasures.find(propEq("measureId", x.measureId))) === null || _measure$submeasures$ === void 0 ? void 0 : _measure$submeasures$.gender);
    }, measurementsWithMetadataRef.current);
    var unitsOrder = sortOrganizationIdsByPeriodValue(lookupOrganization, ((_query$sort = query.sort) === null || _query$sort === void 0 ? void 0 : _query$sort[measure.measureId]) || query.sort, query.periodType, query.dateTo, query.units, measurementsWithMetadataRef.current);
    var sortedMeasurementsWithMetadata = sort(function (x) {
      return unitsOrder.indexOf(x.organizationId);
    }, genderSortedMeasurementsWithMetadata);

    //During a re-render that makes the containerRef smaller we need to reset the svg-width+height, else the containerRef
    //will continue to be the previous large size and not recognize that it can be smaller.
    svg = svg.attr("width", containerRef.current.offsetWidth).attr("height", rowHeight + margin.top + margin.bottom);
    var width = containerRef.current.offsetWidth - margin.left - margin.right;
    var height = svgHeight - margin.top - margin.bottom;
    svg = svg.attr("width", containerRef.current.offsetWidth).attr("height", svgHeight);
    var currentPeriod = query.periodType && query.dateTo && getPeriod(query.periodType, query.dateTo);
    var extractCiLow = function extractCiLow(x) {
      return x && x.confidenceInterval && (x.confidenceInterval.low || x.confidenceInterval.low === 0) || null;
    };
    var extractCiHigh = function extractCiHigh(x) {
      return x && x.confidenceInterval && (x.confidenceInterval.high || x.confidenceInterval.high === 0) || null;
    };
    var measurements = function measurements(x) {
      return x.measurements;
    };
    var sameOrPreviousPeriod = function sameOrPreviousPeriod(x) {
      return samePeriod(x.period, previousPeriod(query.periodType, currentPeriod)) || samePeriod(x.period, currentPeriod);
    };
    var ciOnlyWhenSame = function ciOnlyWhenSame(x) {
      return samePeriod(x.period, currentPeriod) ? [extractValue(null, x), extractCiLow(x), extractCiHigh(x)] : [extractValue(null, x)];
    };
    var values = query.periodType && query.dateTo && sortedMeasurementsWithMetadata.flatMap(measurements).filter(sameOrPreviousPeriod).flatMap(ciOnlyWhenSame) || [];
    var withTargets = values ? targetValues ? values.concat(targetValues) : values : targetValues ? targetValues : [];
    var minValue = query.zoom && getPaddedMin(withTargets);
    minValue = typeof minValue === "number" ? minValue : 0;
    var maxValue = !query.zoom && measureUnit.indexOf("%") !== -1 ? 100 : getPaddedMax(withTargets);
    maxValue = typeof maxValue === "number" && !isNaN(maxValue) ? measureUnit && measureUnit.indexOf("%") !== -1 && maxValue > 100 ? 100 : maxValue : 100;
    var xScale = d3.scaleLinear().domain([minValue, maxValue]).nice().range([0, width]);
    var color = getColorUsingOption(query.colorSettings, activeUnitList);
    var barWidth = compose(xScale, extractValue(xScale.domain()[0]));
    var content = svg.select(".chartData");
    var hydrateScale = function hydrateScale(x) {
      return _objectSpread(_objectSpread({}, x), {}, {
        scaleX: xScale(x.Value)
      });
    };
    hydratedTargets.current = targetMeasurements || [].sort(function (a, b) {
      return (b.Value || b.value) - (a.Value || a.value);
    }).map(hydrateScale);
    var config = {
      measurementsWithMetadata: sortedMeasurementsWithMetadata,
      svg: svg,
      content: content,
      target: query.showTarget,
      previous: query.showPrevious,
      ci: query.showCi,
      barWidth: barWidth,
      dateTo: query.dateTo,
      periodType: query.periodType,
      margin: margin,
      xScale: xScale,
      color: color,
      idxMultiplier: idxMultiplier,
      genderOpacity: genderOpacity,
      barHeight: barHeight,
      calcYPos: calcYPos,
      barPadding: barPadding,
      paddedBarHeight: paddedBarHeight,
      previousBarHeight: previousBarHeight,
      height: height,
      metadata: measure,
      measureUnit: measure === null || measure === void 0 ? void 0 : measure.measureUnit,
      width: width,
      units: query.units,
      organizationLookup: lookupOrganization,
      interactivity: interactivity,
      reichAverage: query.showReich,
      hydratedTargets: hydratedTargets.current
    };
    updateAxes(config);
    updateGridLines(config);
    updateLatestMeasurements(config);
    updatePreviousMeasurements(config);
    updateCi(config);
    updateSoughtValue(config);
    updateReichAverage(config);
    svg.selectAll(".tick").selectAll("line").style("stroke-width", "1px").style("stroke", "#e8e7e6");
    svg.selectAll(".ciLine").style("stroke-width", "1px");
    svg.select("svg").selectAll("*").style("shape-rendering", "crispedges");
  };
  useGraphRender(_objectSpread(_objectSpread(_objectSpread({}, measurementsParams(query)), pick(["zoom", "colorSettings", "sort", "relativeTo", "showTarget", "showReich", "showPrevious", "showCi"], query)), {}, {
    activeUnitList: activeUnitList,
    loading: loading,
    measure: measure,
    measurementsWithMetadata: measurementsWithMetadata
  }), containerRef, renderGraph);
  return /*#__PURE__*/_jsx("svg", {
    ref: graphRef,
    className: "svg_content"
  });
}, function (prev, curr) {
  return equals(prev.query, curr.query) && equals(prev.loading, curr.loading) && equals(prev.measurementsWithMetadata, curr.measurementsWithMetadata) && equals(prev.measure, curr.measure) && equals(prev.activeUnitList, curr.activeUnitList) && equals(prev.focusId, curr.focusId);
});